import React, { useCallback } from 'react';
import { ReactComponent as Add } from '../../../assets/Icons/lucideIcons/plus.svg';
import { ReactComponent as FormatAlignCenter } from '../../../assets/Icons/lucideIcons/text-align-center.svg';
import { ReactComponent as FormatAlignLeft } from '../../../assets/Icons/lucideIcons/text-align-start.svg';
import { ReactComponent as FormatAlignRight } from '../../../assets/Icons/lucideIcons/text-align-end.svg';
import { ReactComponent as Remove } from '../../../assets/Icons/lucideIcons/minus.svg';
import { ReactComponent as WidthFull } from '../../../assets/Icons/lucideIcons/expand.svg';
import { ReactComponent as WidthNormal } from '../../../assets/Icons/lucideIcons/columns-3.svg';
import { BubbleMenu, Editor } from '@tiptap/react';
import PropTypes from 'prop-types';

import {
  BubbleMenuButton,
  BubbleMenuContainer,
  ToolbarVerticalDivider,
} from '../components';

const nodeName = 'resizable-image';

/**
 * @typedef ResizableImageBubbleMenuProps
 * @property {import('@tiptap/react').Editor} editor
 */

const shouldShow = ({ editor }) => editor.isActive(nodeName);

/**
 * @param {ResizableImageBubbleMenuProps} props
 */
const ResizableImageBubbleMenu = ({ editor }) => {
  const handleWidthIncrease = useCallback(() => {
    const currentWidth =
      editor.getAttributes(nodeName).width === '100%'
        ? 300
        : editor.getAttributes(nodeName).width;
    editor.commands.updateAttributes(nodeName, {
      width: Number(currentWidth) + 50,
    });
  }, [editor]);
  const handleWidthDecrease = useCallback(() => {
    const currentWidth =
      editor.getAttributes(nodeName).width === '100%'
        ? 300
        : editor.getAttributes(nodeName).width;
    editor.commands.updateAttributes(nodeName, {
      width: Number(currentWidth) - 50,
    });
  }, [editor]);

  const setDefaultWidth = useCallback(() => {
    editor.commands.updateAttributes(nodeName, {
      width: 300,
    });
  }, [editor]);

  const setFullWidth = useCallback(() => {
    editor.commands.updateAttributes(nodeName, {
      width: '100%',
    });
  }, [editor]);

  const getImageAlignmentHandler = (alignment) => {
    return () => {
      editor.commands.updateAttributes(nodeName, {
        alignment,
      });
    };
  };

  return (
    <BubbleMenu
      editor={editor}
      pluginKey="resizableImageMenu"
      shouldShow={shouldShow}
      tippyOptions={{
        offset: [0, 20],
      }}
    >
      <BubbleMenuContainer>
        <BubbleMenuButton
          onClick={setDefaultWidth}
          sx={{
            gap: 0.5,
          }}
        >
          <WidthNormal width={18} height={18} /> Normal
        </BubbleMenuButton>
        <ToolbarVerticalDivider />
        <BubbleMenuButton
          onClick={setFullWidth}
          sx={{
            gap: 0.5,
          }}
        >
          <WidthFull width={18} height={18} /> Full width
        </BubbleMenuButton>
        <ToolbarVerticalDivider />
        <BubbleMenuButton onClick={handleWidthDecrease}>
          <Remove width={18} height={18} />
        </BubbleMenuButton>
        <BubbleMenuButton onClick={handleWidthIncrease}>
          <Add width={18} height={18} />
        </BubbleMenuButton>
        <ToolbarVerticalDivider />
        <BubbleMenuButton onClick={getImageAlignmentHandler('start')}>
          <FormatAlignLeft width={18} height={18} />
        </BubbleMenuButton>
        <BubbleMenuButton onClick={getImageAlignmentHandler('center')}>
          <FormatAlignCenter width={18} height={18} />
        </BubbleMenuButton>
        <BubbleMenuButton onClick={getImageAlignmentHandler('end')}>
          <FormatAlignRight width={18} height={18} />
        </BubbleMenuButton>
      </BubbleMenuContainer>
    </BubbleMenu>
  );
};
ResizableImageBubbleMenu.propTypes = {
  editor: PropTypes.instanceOf(Editor).isRequired,
};

export default ResizableImageBubbleMenu;
